/**
 * Reads query parameters from both the page URL and the Descope hash route.
 */
export const getDescopeQueryParams = (): URLSearchParams => {
  const hash = window.location.hash;
  const hashQueryIndex = hash.indexOf('?');
  const params = new URLSearchParams(window.location.search);

  if (hashQueryIndex !== -1) {
    const hashParams = new URLSearchParams(hash.substring(hashQueryIndex));
    hashParams.forEach((value, key) => {
      params.set(key, value);
    });
  }

  return params;
};

/**
 * Determines if the current session is from a mobile app by checking
 * the is_mobile_app query parameter in the URL hash route.
 * This param is set by cerberus in the FlowView URL.
 * @returns boolean indicating if this is a mobile app session
 */
export function isMobileApp(): boolean {
  const params = getDescopeQueryParams();
  return params.get('is_mobile_app') === 'true';
}

export function isMobileSmsAuthEnabled(): boolean {
  const params = getDescopeQueryParams();
  return (
    params.get('is_mobile_app') === 'true' &&
    params.get('is_mobile_sms_enabled') === 'true'
  );
}

/**
 * Whether SMS auth is enabled for a plain web session (no mobile app WebView).
 * Mirrors the `is_web_sms_enabled` param the universal-login Descope flow reads
 * in its "Pass mobile app query params" scriptlet, so the Descope-hosted
 * rendering and this app agree on when the phone option is offered. Unlike
 * `is_mobile_sms_enabled` this stands alone — there is no `is_mobile_app` to
 * pair it with.
 * @returns boolean indicating if web SMS auth is enabled
 */
export function isWebSmsAuthEnabled(): boolean {
  return getDescopeQueryParams().get('is_web_sms_enabled') === 'true';
}

/**
 * Whether the QA-only web SMS signup option is enabled.
 */
export function isSmsSignupEnabled(): boolean {
  return getDescopeQueryParams().get('is_sms_signup_enabled') === 'true';
}

/**
 * Returns the mobile device's stable id forwarded by cerberus in the FlowView
 * URL (mobile_stable_id). This is the same value cerberus uses as the Statsig
 * stableID, so the web flow can evaluate stableID-targeted gates with the same
 * identity as the native app. Falls back to the deviceId injected on
 * window.__MOBILE_APP_CONTEXT__ when the query param is absent.
 * @returns the mobile stable id, or null when not in a mobile app session
 */
export function getMobileStableId(): string | null {
  const params = getDescopeQueryParams();
  const fromParam = params.get('mobile_stable_id');
  if (fromParam) {
    return fromParam;
  }

  const context = (
    window as unknown as { __MOBILE_APP_CONTEXT__?: { deviceId?: string } }
  ).__MOBILE_APP_CONTEXT__;

  return context?.deviceId ?? null;
}
